import { useCallback, useEffect, useState, type ReactNode } from "react";

/** How long an announcement stays in the region; later it would only linger for a virtual cursor. */
export const ANNOUNCEMENT_CLEAR_MS = 6000;

/**
 * A polite live region for short status messages. Plain state is not enough: announcing the same
 * text twice sets an identical string, React skips the render and a screen reader says nothing
 * the second time. Every call here renders a NEW node (a fresh `key`) inside the region, which
 * is what a screen reader announces, and the text is cleared after a while.
 *
 * Render `region` once, where the message belongs; call `announce(text)` from the event (or
 * while rendering, to react to a prop change).
 */
export function useLiveAnnouncement(): {
  announce: (text: string) => void;
  region: ReactNode;
} {
  const [current, setCurrent] = useState<{ id: number; text: string } | null>(
    null,
  );

  // The only side effect is here, so `announce` is a plain state update and may also be called
  // while rendering (reacting to a prop change). A new announcement replaces `current`, which
  // restarts the clock.
  useEffect(() => {
    if (!current) return;
    const timer = setTimeout(() => setCurrent(null), ANNOUNCEMENT_CLEAR_MS);
    return () => clearTimeout(timer);
  }, [current]);

  const announce = useCallback((text: string) => {
    setCurrent((previous) => ({ id: (previous?.id ?? 0) + 1, text }));
  }, []);

  const region = (
    <span role="status" className="sr-only">
      {current && <span key={current.id}>{current.text}</span>}
    </span>
  );
  return { announce, region };
}
